import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { CalendarCheck, LayoutGrid, Repeat, Zap, UtensilsCrossed, Blocks, HeartHandshake, NotebookPen, Sprout } from "lucide-react";
import { ECOSYSTEM } from "../config/brand";
import { FadeUp, LineReveal, Eyebrow } from "../components/Reveal";
import { hoverOnly } from "../components/Motion";

const icons = [CalendarCheck, LayoutGrid, Repeat, Zap, UtensilsCrossed, Blocks, HeartHandshake, NotebookPen, Sprout];

export const Ecosystem = () => {
  const [active, setActive] = useState(null);

  const node = (n, i, delay) => {
    const Icon = icons[i];
    const isActive = active === i;
    return (
      <FadeUp key={n.label} delay={delay}>
        <button
          type="button"
          data-testid={`ecosystem-${n.label.toLowerCase()}`}
          aria-pressed={isActive}
          {...hoverOnly({
            onMouseEnter: () => setActive(i),
            onMouseLeave: () => setActive(null),
          })}
          onFocus={() => setActive(i)}
          onBlur={() => setActive(null)}
          onClick={() => setActive(i)}
          className={`flex h-full w-full flex-col items-center rounded-3xl border bg-warmwhite p-6 text-center transition-[border-color,box-shadow,transform] duration-300 ${
            isActive
              ? "-translate-y-1 border-rose/60 shadow-[0_18px_44px_rgba(90,48,73,0.14)]"
              : "border-mauve/35 shadow-[0_8px_24px_rgba(90,48,73,0.04)]"
          }`}
        >
          <span className={`flex h-11 w-11 items-center justify-center rounded-xl transition-colors duration-300 ${isActive ? "bg-plum text-ivory" : "bg-blushcream text-plum"}`}>
            <Icon size={18} strokeWidth={1.5} />
          </span>
          <p className="mt-3 text-xs font-bold uppercase tracking-[0.2em] text-rose">{n.label}</p>
          <p className="mt-1 text-[13px] leading-snug text-plumcharcoal/65">{n.tool}</p>
        </button>
      </FadeUp>
    );
  };

  return (
    <section className="bg-ivory" aria-label="The product ecosystem" data-testid="ecosystem-section">
      <div className="mx-auto max-w-[1280px] px-5 py-20 sm:px-8 lg:py-28">
        <div className="mx-auto max-w-3xl text-center">
          <FadeUp>
            <Eyebrow>The ecosystem</Eyebrow>
          </FadeUp>
          <LineReveal
            as="h2"
            lines={["One system for the whole", "of your working-mother life."]}
            className="mt-4 font-serif text-4xl font-medium leading-[1.08] tracking-tight text-plum sm:text-5xl"
          />
          <FadeUp delay={0.1}>
            <p className="mt-4 text-sm font-semibold uppercase tracking-[0.18em] text-plumcharcoal/45">
              Tap any area — everything connects
            </p>
          </FadeUp>
        </div>

        <div className="mx-auto mt-14 grid max-w-4xl grid-cols-2 gap-4 sm:grid-cols-3" data-testid="ecosystem-diagram">
          {ECOSYSTEM.slice(0, 4).map((n, i) => node(n, i, i * 0.05))}

          <FadeUp delay={0.2} className="col-span-2 sm:col-span-1 sm:row-span-1">
            <motion.div
              animate={active !== null ? { scale: 1.04 } : { scale: 1 }}
              transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
              className={`flex h-full flex-col items-center justify-center rounded-3xl bg-plum p-8 text-center transition-shadow duration-300 ${
                active !== null
                  ? "shadow-[0_26px_60px_rgba(201,130,150,0.4)] ring-2 ring-gold/60"
                  : "shadow-[0_20px_50px_rgba(90,48,73,0.3)]"
              }`}
              data-testid="ecosystem-hub"
            >
              <img src="/assets/logo-mark.png" alt="" className="h-12 w-auto" width={40} height={53} loading="lazy" />
              <AnimatePresence mode="wait">
                <motion.div
                  key={active === null ? "default" : ECOSYSTEM[active].label}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -6 }}
                  transition={{ duration: 0.22 }}
                >
                  <p className="mt-3 font-serif text-2xl font-semibold tracking-tight text-ivory">
                    {active === null ? "BloomParentify" : ECOSYSTEM[active].label}
                  </p>
                  <p className="mt-1 text-[11px] font-bold uppercase tracking-[0.22em] text-gold">
                    {active === null ? "The complete system" : ECOSYSTEM[active].tool}
                  </p>
                </motion.div>
              </AnimatePresence>
            </motion.div>
          </FadeUp>

          {ECOSYSTEM.slice(4).map((n, i) => node(n, i + 4, 0.1 + i * 0.05))}
        </div>
      </div>
    </section>
  );
};
